import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronDown, ChevronUp, Copy, Check, Activity } from 'lucide-react';
import { isLocalhostRuntime } from '@/lib/adminAccess';

/**
 * JobMonitor - Development tool for monitoring async job status
 * Shows trace IDs, timing, polling attempts, and diagnostic information
 * Only visible when VITE_ENABLE_MONITOR=true.
 */
const JobMonitor = ({ jobId, traceId, status, elapsedSeconds, pollingCount, error = null }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logs, setLogs] = useState([]);
  
  // Keep diagnostic overlays out of the product UI unless explicitly enabled.
  const showMonitor =
    isLocalhostRuntime() &&
    import.meta.env.VITE_ENABLE_MONITOR === 'true';

  const copyTraceId = () => {
    navigator.clipboard.writeText(traceId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'running':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Monitor console logs (simplified version)
  useEffect(() => {
    if (!showMonitor) return undefined;

    const originalLog = console.log;
    const originalError = console.error;

    const serializeArg = (arg) => {
      if (arg === null || arg === undefined) return String(arg);
      if (typeof arg === 'string') return arg;
      if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
      if (arg instanceof Error) {
        return `${arg.name}: ${arg.message}`;
      }

      if (typeof window !== 'undefined') {
        if (arg instanceof HTMLElement) {
          return `<${arg.tagName.toLowerCase()} id="${arg.id || 'n/a'}" class="${arg.className || ''}">`;
        }
        if (arg instanceof HTMLCanvasElement) {
          return `<canvas id="${arg.id || 'canvas'}" width="${arg.width}" height="${arg.height}">`;
        }
      }

      try {
        const seen = new WeakSet();
        return JSON.stringify(arg, (key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
              return '[Circular]';
            }
            seen.add(value);
          }
          return value;
        });
      } catch {
        return '[Unserializable Object]';
      }
    };

    const addLog = (type, args) => {
      const message = args.map(serializeArg).join(' ');

      if (message.includes(traceId) || message.includes(jobId)) {
        setLogs(prev => [...prev.slice(-20), {
          timestamp: new Date().toISOString(),
          type,
          message: message.substring(0, 200)
        }]);
      }
    };

    console.log = (...args) => {
      addLog('log', args);
      originalLog(...args);
    };

    console.error = (...args) => {
      addLog('error', args);
      originalError(...args);
    };

    return () => {
      console.log = originalLog;
      console.error = originalError;
    };
  }, [traceId, jobId, showMonitor]);

  // Don't render if monitoring is disabled
  if (!showMonitor) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[99999] max-w-md">
      <Card className="bg-white/95 backdrop-blur-sm border-2 border-blue-500 shadow-2xl">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600 animate-pulse" />
              <span className="font-semibold text-sm text-gray-900">Job Monitor</span>
              <Badge variant="outline" className="text-xs">DEV</Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-6 w-6 p-0"
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          </div>

          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Status:</span>
              <Badge className={getStatusColor(status)}>{status}</Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Elapsed:</span>
              <span className="font-mono font-medium">{elapsedSeconds}s</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Polls:</span>
              <span className="font-mono font-medium">{pollingCount}</span>
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-600">Trace ID:</span>
              <div className="flex items-center gap-1">
                <span className="font-mono text-[10px] truncate max-w-[120px]">{traceId}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyTraceId}
                  className="h-5 w-5 p-0"
                  title="Copy trace ID"
                >
                  {copied ? (
                    <Check 
                      className="h-3 w-3 fill-current" 
                      style={{ color: '#60A5FA' }} 
                    />
                  ) : (
                    <Copy 
                      className="h-3 w-3" 
                      style={{ color: '#60A5FA' }} 
                    />
                  )}
                </Button>
              </div>
            </div>

            {jobId && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Job ID:</span>
                <span className="font-mono text-[10px] truncate max-w-[150px]">{jobId}</span>
              </div>
            )}

            {error && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-800">
                <div className="font-semibold mb-1">Error:</div>
                <div className="text-[10px]">{error}</div>
              </div>
            )}
          </div>

          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs font-semibold text-gray-700 mb-2">Recent Logs ({logs.length}):</div>
              <div className="max-h-48 overflow-y-auto space-y-1 bg-gray-50 rounded p-2">
                {logs.length === 0 ? (
                  <div className="text-gray-500 text-[10px]">No logs yet...</div>
                ) : (
                  logs.map((log, idx) => (
                    <div key={idx} className={`text-[10px] font-mono ${log.type === 'error' ? 'text-red-600' : 'text-gray-700'}`}>
                      <span className="text-gray-400">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      {' '}
                      <span className={log.type === 'error' ? 'text-red-600' : 'text-blue-600'}>
                        {log.type.toUpperCase()}
                      </span>
                      {': '}
                      {log.message}
                    </div>
                  ))
                )}
              </div>
              
              <div className="mt-3 text-[10px] text-gray-500">
                <div>💡 Tip: Check browser console for full trace logs</div>
                <div className="mt-1">🔍 Search for: [TRACE-{traceId.substring(0, 20)}...]</div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default JobMonitor;
